import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Modal,
  Snackbar,
  SnackbarContent,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import { Prompt } from "react-router";
import { useParams } from "react-router-dom";
import { useAuth } from "../sdk";
import {
  ActualDemandLevelMultiplier,
  DayOfWeekMultiplier,
  LeadTimeMultiplier,
  ModelBody,
} from "./components";
import { Container, PageContainer } from "./Styles";
import { useDayOfWeek, useDemandLevel, useLeadTime } from "./hooks";
import { useLastDay } from "./hooks/useLastDay";
import { EnableDayMultiplier } from "./components/EnableDayMultiplier";
import { UploadBtn } from "../BusinessOnBooks/Styles";
import { Publish } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

const MULTIPLIER_CONFIG = {
  ActualDemandLevel: "Actual Demand Level",
  DayOfWeek: "Day Of Week",
  LeadTime: "Lead Time",
  LastDay: "Last Day",
};

const initialMultipliers = {
  isDemandLevel: false,
  isDayOfWeek: false,
  isLeadTime: false,
  isLastDay: false,
};

const MULToUpdate = {
  ActualDemandLevel: false,
  DayOfWeek: false,
  LeadTime: false,
  LastDay: false,
};

// Multipliers page: allows viewing/editing demand, day-of-week, lead-time and last-day multipliers,
// plus uploading a last-day multiplier file (anchored panel next to the Upload button) and copying configs across hotels.

export default function ({ openSelectbar, setOpenSelectBar }) {
  const { hotelId } = useParams();
  const [copyFromHotelId, setCopyFromHotelId] = useState(null);
  const [copyHotelName, setCopyHotelName] = useState("");
  const [updateCheckbox, setUpdateCheckbox] = useState(false);
  const { token, authFetch, permission, hotels } = useAuth();
  const [updateSelectedMul, setupdateSelectedMul] = useState(MULToUpdate);
  const [mulUploadOpen, setMulUploadOpen] = useState(false);
  const [mulUploadMsg, setMulUploadMsg] = useState("");
  const [mulUploadError, setMulUploadError] = useState(false);
  const [mulFileName, setMulFileName] = useState("");
  const [mulUploading, setMulUploading] = useState(false);
  const mulFormData = useRef(new FormData());
  const mulPopupRef = useRef(null);
  const mulUploadBtnRef = useRef(null);
  const [selectMUL, setSelectMUL] = useState([]);
  const [allHotels, setAllHotels] = useState();

  const getAllHotels = useCallback(async () => {
    const isAdmin = Object.values(permission || {}).some(
      (perm) => perm?.name === "acrossHotel"
    );

    if (isAdmin) {
      const { get } = await authFetch({
        path: `/hotel`,
      });
      const { data, error } = await get();
      if (data) {
        setAllHotels(data);
      } else {
        console.log(error);
      }
    } else {
      setAllHotels(hotels);
    }
  }, [authFetch, hotels, permission]);

  const isMultiplierAdmin = Object.values(permission || {}).some(
    (perm) => perm?.name === "uploadLastDayMultiplier"
  );

  useEffect(() => {
    if (token) {
      getAllHotels();
    }
  }, [token, getAllHotels]);
  const handleMULChange = (val) => {
    setSelectMUL(val);
  };
  const updateDLF = () => {
    setOpenSelectBar(false);
    setupdateSelectedMul({
      ActualDemandLevel: selectMUL.some(
        (val) => val === MULTIPLIER_CONFIG.ActualDemandLevel
      ),
      DayOfWeek: selectMUL.some((val) => val === MULTIPLIER_CONFIG.DayOfWeek),
      LastDay: selectMUL.some((val) => val === MULTIPLIER_CONFIG.LastDay),
      LeadTime: selectMUL.some((val) => val === MULTIPLIER_CONFIG.LeadTime),
    });
  };

  const {
    demandLevels,
    dlIsInEditMode,
    setDlIsInEditMode,
    dlIsCancelled,
    dlSetIsCancelled,
    dlIsSaved,
    dlSetIsSaved,
    dlChangeHandler,
    dlopen,
    dlhandleCloseStatus,
    dlnetworkMsg,
    dlLoading,
    editDemandLevels,
    handleEditDemandsLevel,
    checkDemandLevelValidation,
    handleDeleteDemandsLevel,
  } = useDemandLevel(
    hotelId,
    copyFromHotelId,
    updateSelectedMul.ActualDemandLevel
  );
  const {
    daysOfWeek,
    dowIsInEditMode,
    setDowIsInEditMode,
    dowIsCancelled,
    dowSetIsCancelled,
    dowIsSaved,
    dowSetIsSaved,
    dowChangeHandler,
    dowopen,
    dowhandleCloseStatus,
    downetworkMsg,
    dowLoading,
    editDaysOfWeek,
    handleEditDaysOfWeekMultiplierChange,
  } = useDayOfWeek(hotelId, copyFromHotelId, updateSelectedMul.DayOfWeek);
  const {
    leadTimes,
    ltIsInEditMode,
    setLtIsInEditMode,
    ltIsCancelled,
    ltSetIsCancelled,
    ltIsSaved,
    ltSetIsSaved,
    ltChangeHandler,
    ltopen,
    lthandleCloseStatus,
    ltnetworkMsg,
    ltonRemoveRowHandler,
    ltonAddRowHandler,
    ltLoading,
    editLeadTimeMultiplier,
    handleEditLeadTimeMultiplierChange,
    checkLeadTimeMultiplierValidation,
  } = useLeadTime(hotelId, copyFromHotelId, updateSelectedMul.LeadTime);
  const {
    lastDayData,
    ldSetIsCancelled,
    ldSetIsSaved,
    ldOpen,
    ldHandleCloseStatus,
    ldNetworkMsg,
    ldLoading,
    updateLastDayData,
    deleteTimeSlotRow,
    deleteRowInTimeSlot,
    ldEditMode,
    ldSetEditMode,
  } = useLastDay(hotelId, copyFromHotelId, updateSelectedMul.LastDay);

  const [multipliers, setMultipliers] = useState(initialMultipliers);

  const handleMultiplier = (key, value) => {
    setMultipliers({
      ...multipliers,
      [key]: value,
    });
    setUpdateCheckbox((prev) => !prev);
  };

  useEffect(() => {
    async function GetMultipliers() {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/hotel-pricing-multiplier`,
      });
      const { data, error } = await get();
      if (data) {
        setMultipliers(data);
      } else {
        console.log(error);
      }
    }
    if (token) {
      GetMultipliers();
    }
  }, [token, authFetch, hotelId]);

  useEffect(() => {
    if (multipliers.id) {
      async function UpdateMultipliers() {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/hotel-pricing-multiplier`,
        });
        const { data, error } = await put(multipliers);
        setMultipliers(multipliers);
        if (data) {
        } else {
          console.log(error);
        }
      }
      UpdateMultipliers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCheckbox]);

  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(false);

  useEffect(() => {
    setShouldBlockNavigation(
      ltIsInEditMode || dowIsInEditMode || dlIsInEditMode
    );
  }, [ltIsInEditMode, dowIsInEditMode, dlIsInEditMode]);
  useEffect(() => {
    if (openSelectbar) setupdateSelectedMul(MULToUpdate);
  }, [openSelectbar]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mulPopupRef.current &&
        !mulPopupRef.current.contains(event.target) &&
        mulUploadBtnRef.current &&
        !mulUploadBtnRef.current.contains(event.target)
      ) {
        handleMulUploadClose();
      }
    };

    if (mulUploadOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [mulUploadOpen]);

  const handleMulFileChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    mulFormData.current = new FormData();
    mulFormData.current.append("file", file);
    setMulFileName(file.name);
  };

  const handleMulUpload = async () => {
    if (!mulFormData.current.has("file")) {
      setMulUploadMsg("Please select a file to upload");
      setMulUploadError(true);
      return;
    }
    setMulUploading(true);
    const { post } = await authFetch({
      path: `/hotel/${hotelId}/upload-last-day-multiplier`,
    });
    const { data, response, error } = await post(mulFormData.current);

    if (response?.status === 200) {
      setMulUploadMsg("Upload Successful");
      setMulUploadError(false);
      setMulUploadOpen(false);
      setMulFileName("");
      mulFormData.current = new FormData();
    
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      setMulUploadMsg(data?.messageToUser || error?.message || "Upload failed");
      setMulUploadError(true);
    }
    setMulUploading(false);
  };

  const handleMulUploadClose = () => {
    setMulUploadOpen(false);
    setMulFileName("");
    mulFormData.current = new FormData();
  };

  const enableDayConfigs = [
    {
      title: "Enable Current Day",
      checkboxKey: "isCurrentDay",
      dayKey: "current_day",
    },
    {
      title: "Enable Next Day",
      checkboxKey: "isNextDay",
      dayKey: "next_day",
    },
    {
      title: "Enable Day After Next Day",
      checkboxKey: "isDayAfterTomorrow",
      dayKey: "day_after_tomorrow",
    },
  ];
  return (
    <PageContainer>
      <Prompt
        when={shouldBlockNavigation}
        message="You have unsave changes, are you sure want to leave"
      />
      {/* <LocalizationProvider dateAdapter={AdapterDateFns}> */}
      <Modal
        open={openSelectbar}
        onClose={() => {
          setOpenSelectBar(false);
        }}
      >
        <div>
          <ModelBody
            copyFromHotelId={copyFromHotelId}
            setCopyFromHotelId={setCopyFromHotelId}
            copyHotelName={copyHotelName}
            setCopyHotelName={setCopyHotelName}
            handleMULChange={handleMULChange}
            selectMUL={selectMUL}
            updateDLF={updateDLF}
            setOpenSelectBar={setOpenSelectBar}
            allHotels={allHotels}
            hotelId={hotelId}
          />
        </div>
      </Modal>
      <Container>
        <ActualDemandLevelMultiplier
          demandLevels={demandLevels}
          isInEditMode={dlIsInEditMode}
          setIsInEditMode={setDlIsInEditMode}
          isCancelled={dlIsCancelled}
          setIsCancelled={dlSetIsCancelled}
          isSaved={dlIsSaved}
          setIsSaved={dlSetIsSaved}
          onChange={dlChangeHandler}
          multipliers={multipliers}
          handleMultiplier={handleMultiplier}
          open={dlopen}
          handleCloseStatus={dlhandleCloseStatus}
          networkMsg={dlnetworkMsg}
          dlLoading={dlLoading}
          editDemandLevels={editDemandLevels}
          handleEditDemandsLevel={handleEditDemandsLevel}
          checkDemandLevelValidation={checkDemandLevelValidation}
          handleDeleteDemandsLevel={handleDeleteDemandsLevel}
        />

        <DayOfWeekMultiplier
          daysOfWeek={daysOfWeek}
          isInEditMode={dowIsInEditMode}
          setIsInEditMode={setDowIsInEditMode}
          isCancelled={dowIsCancelled}
          setIsCancelled={dowSetIsCancelled}
          isSaved={dowIsSaved}
          setIsSaved={dowSetIsSaved}
          onChange={dowChangeHandler}
          multipliers={multipliers}
          handleMultiplier={handleMultiplier}
          open={dowopen}
          handleCloseStatus={dowhandleCloseStatus}
          networkMsg={downetworkMsg}
          dowLoading={dowLoading}
          editDaysOfWeek={editDaysOfWeek}
          handleEditDaysOfWeekMultiplierChange={
            handleEditDaysOfWeekMultiplierChange
          }
        />
        <LeadTimeMultiplier
          leadTimes={leadTimes}
          isInEditMode={ltIsInEditMode}
          setIsInEditMode={setLtIsInEditMode}
          isCancelled={ltIsCancelled}
          setIsCancelled={ltSetIsCancelled}
          isSaved={ltIsSaved}
          setIsSaved={ltSetIsSaved}
          onChange={ltChangeHandler}
          multipliers={multipliers}
          handleMultiplier={handleMultiplier}
          open={ltopen}
          handleCloseStatus={lthandleCloseStatus}
          networkMsg={ltnetworkMsg}
          ltonRemoveRowHandler={ltonRemoveRowHandler}
          ltonAddRowHandler={ltonAddRowHandler}
          ltLoading={ltLoading}
          editLeadTimeMultiplier={editLeadTimeMultiplier}
          handleEditLeadTimeMultiplierChange={
            handleEditLeadTimeMultiplierChange
          }
          checkLeadTimeMultiplierValidation={checkLeadTimeMultiplierValidation}
        />

        {isMultiplierAdmin && (
          <Box
            sx={{
              gridColumn: "1 / -1",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              padding: "10px 10px",
              position: "relative",
            }}
          >
            <UploadBtn
              ref={mulUploadBtnRef}
              style={{
                boxShadow: "0px 4px 4px rgba(48, 111, 188, 0.2)",
                borderRadius: "8px",
                background: "#306FBC",
                color: "#FFFFFF",
                textTransform: "none",
                width: "fit-content",
              }}
              onClick={() => {
                setMulUploadOpen((prev) => !prev);
                setMulUploadMsg("");
                setMulUploadError(false);
                mulFormData.current = new FormData();
                setMulFileName("");
              }}
            >
              Upload Last Day Multiplier
              <Publish />
            </UploadBtn>

            {mulUploadOpen && (
              <Box
                ref={mulPopupRef}
                sx={{
                  position: "absolute",
                  top: 45,
                  right: 0,
                  background: "white",
                  width: 360,
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  zIndex: 10,
                }}
              >
                <IconButton
                  onClick={handleMulUploadClose}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <div sx={{ typography: "h6", fontWeight: "bold" }}>
                  Upload Last Day Multiplier
                </div>
                <input
                  id="mul-file-input"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleMulFileChange}
                />
                <label htmlFor="mul-file-input">
                  <UploadBtn
                    component="span"
                    style={{
                      boxShadow: "0px 4px 4px rgba(48, 111, 188, 0.2)",
                      borderRadius: "8px",
                      background: "#306FBC",
                      color: "#FFFFFF",
                      textTransform: "none",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    Choose File
                    <Publish />
                  </UploadBtn>
                </label>
                <div
                  style={{
                    minHeight: "20px",
                    font: "normal normal 500 14px/18px Roboto",
                    color: "#000",
                    textAlign: "center",
                  }}
                >
                  {mulFileName || "No file selected"}
                </div>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    onClick={handleMulUpload}
                    disabled={mulUploading}
                  >
                    {mulUploading ? "Uploading..." : "Upload"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
        {enableDayConfigs.map((config) => (
          <EnableDayMultiplier
            key={config.dayKey}
            title={config.title}
            checkboxKey={config.checkboxKey}
            dayKey={config.dayKey}
            apiResponseData={lastDayData}
            setIsCancelled={ldSetIsCancelled}
            setIsSaved={ldSetIsSaved}
            multipliers={multipliers}
            handleMultiplier={handleMultiplier}
            open={ldOpen}
            handleCloseStatus={ldHandleCloseStatus}
            networkMsg={ldNetworkMsg}
            loading={ldLoading}
            updateLastDayData={updateLastDayData}
            deleteTimeSlotRow={deleteTimeSlotRow}
            deleteRowInTimeSlot={deleteRowInTimeSlot}
            editMode={ldEditMode}
            setEditMode={ldSetEditMode}
          />
        ))}
      </Container>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!mulUploadMsg}
        autoHideDuration={4000}
        onClose={() => setMulUploadMsg("")}
      >
        {mulUploadMsg && (
          <SnackbarContent
            style={{
              backgroundColor: mulUploadError ? "#CA3433" : "#228b22",
            }}
            message={mulUploadMsg}
          />
        )}
      </Snackbar>
      {/* </LocalizationProvider> */}
    </PageContainer>
  );
}
